/**
 * Design-aware scoring profiles and the study type weight table (design-documents/025, 039).
 *
 * Separate from scorecard.ts (which owns the 13 scored dimensions and their labels) because
 * this module is a distinct concern layered on top: which of those dimensions apply to a given
 * study type, how much cross-design weight that design itself carries, and the quality rating
 * the resulting score is shown with. Consumers:
 *   - claim_verification/internal/search.ts (rerank: profile selection, design weight)
 *   - corpus/internal/scorecard_scoring.ts (ingest gating: effectiveDimensions)
 *   - the platform's quality rating (rateCrossDesignQuality)
 *
 * No Deno-specific or npm imports — plain TypeScript importable by both runtimes.
 */

import { INGEST, isStudyTypeLabel, SCORECARD, SEARCH_CONFIG } from "./scorecard.ts";
import type { DimensionConfig, ScorecardKey, StudyTypeLabel } from "./scorecard.ts";
import type { DimensionReviewState } from "@shared/types/corpus/models.ts";

/** Every scorecard dimension. */
export const ALL_DIMENSIONS = Object.keys(SCORECARD) as ScorecardKey[];

/**
 * Picks which scorecard dimensions count toward a source's execution quality score for a
 * given study type. Boolean membership only — no per-label weighting or point overrides.
 * Never includes "study_type" — see ALL_DIMENSIONS/DEFAULT_SCORING_PROFILE.
 */
export interface DesignScoringProfile {
  /** Scorecard dimensions counted toward this design's score. Omitted dimensions don't
   *  penalize or reward — they're excluded from both the numerator and the max. */
  includedDimensions: ScorecardKey[];
}

/** ALL_DIMENSIONS minus the given keys — the declaration primitive every profile below is
 *  built from, so a profile reads as "every dimension except these" rather than a filter/cast. */
function allDimensionsExcept(...excluded: ScorecardKey[]): ScorecardKey[] {
  return ALL_DIMENSIONS.filter((d) => !excluded.includes(d));
}

/** A DesignScoringProfile excluding the given dimensions, on top of study_type (always
 *  excluded — see DEFAULT_SCORING_PROFILE). */
function profileExcluding(...excluded: ScorecardKey[]): DesignScoringProfile {
  return { includedDimensions: allDimensionsExcept("study_type", ...excluded) };
}

/**
 * The default profile — every dimension except study_type, standard point values.
 * Used both when study_type is untrusted, and when it's trusted but has no entry in
 * DESIGN_SCORING_PROFILES. study_type is excluded here unconditionally — it's never a
 * scored dimension, only a weight (see design-documents/025's formula section).
 */
export const DEFAULT_SCORING_PROFILE: DesignScoringProfile = profileExcluding();

const CASE_CONTROL_CROSS_SECTIONAL_OBSERVATIONAL = profileExcluding("retention_rate");

/** No comparison group, so there is nothing to confound against. */
const NO_CONFOUNDING = profileExcluding("confounding");

const ANIMAL_CELL = profileExcluding(
  "sampling_method",
  "sample_size",
  "retention_rate",
  "population_diversity",
  "confounding",
  "statistical_power"
);

/**
 * Per-design profiles, layering FURTHER exclusions on top of DEFAULT_SCORING_PROFILE. Derived
 * directly from Product's "Relevant annotation fields per study type" (see design-documents/025,
 * step 1). "study_type" must never appear in any entry here — selectScoringProfile strips it
 * defensively regardless, so a mistaken entry can't reintroduce the double-count.
 *
 * Non-Randomised Controlled Trial and the two comparative cohorts are fully applicable across every
 * dimension — no entry needed, they fall through to DEFAULT_SCORING_PROFILE. A stored label from
 * the retired vocabulary has no entry either, so it gets the default profile until it is
 * re-annotated.
 */
export const DESIGN_SCORING_PROFILES: Partial<Record<StudyTypeLabel, DesignScoringProfile>> = {
  RCT: NO_CONFOUNDING,
  "Uncontrolled Trial": NO_CONFOUNDING,
  "Uncontrolled Prospective Cohort": NO_CONFOUNDING,
  "Uncontrolled Retrospective Cohort": NO_CONFOUNDING,
  "Case-Control": CASE_CONTROL_CROSS_SECTIONAL_OBSERVATIONAL,
  "Cross-Sectional": CASE_CONTROL_CROSS_SECTIONAL_OBSERVATIONAL,
  "Other Observational": CASE_CONTROL_CROSS_SECTIONAL_OBSERVATIONAL,
  Animal: ANIMAL_CELL,
  Cell: ANIMAL_CELL,
  "Animal and Cell": ANIMAL_CELL,
  Anecdotal: profileExcluding(
    "sampling_method",
    "sample_size",
    "retention_rate",
    "population_diversity",
    "confounding",
    "statistical_rigor",
    "statistical_power"
  ),
};

/**
 * Study type weight, one per SCORECARD.study_type leaf, approved by Product
 * (design-documents/039 §2). It multiplies execution quality into cross_design_quality, so it
 * scales a source's say in the verdict and its rank among sources of equal relevance.
 *
 * The weights follow the NHMRC evidence levels. NHMRC grades an intervention question (does X
 * work?) and an aetiology question (is X linked to Y?) on separate scales with the same level
 * names; most of our claims are aetiology questions, so observational designs take their
 * aetiology level and trials their intervention level, and levels compare across the two scales.
 * Every level down costs 0.1 and designs on the same level weigh the same, so no gap between two
 * designs is set by hand except the one at the top (see the RCT).
 */
export const STUDY_TYPE_WEIGHTS: Record<StudyTypeLabel, number> = {
  // ── Randomised, with a concurrent control ────────────────────────────────────────────────
  // Level II on the intervention scale, and the anchor. Randomisation is the one feature that
  // removes confounding by allocation instead of adjusting for it, so the RCT stands two levels
  // (0.2) above everything else, including the prospective cohort that shares level II on the
  // aetiology scale.
  RCT: 1.0,

  // ── A concurrent comparison group in humans ──────────────────────────────────────────────
  // The finding is measured against a group studied the same way at the same time, so the
  // design itself answers "compared with what?". Ordered by how much bias the design leaves.
  //   Level II (aetiology): exposure recorded before the outcome, follow-up planned in advance.
  "Comparative Prospective Cohort": 0.8,
  //   Level III-1 (intervention): the investigator assigns the intervention, but not at random,
  //   so who receives it can still differ from who does not.
  "Non-Randomised Controlled Trial": 0.7,
  //   Level III-2 (aetiology): exposure on record before the outcome, but read from records kept
  //   for another purpose, with variables chosen after the outcomes are known.
  "Comparative Retrospective Cohort": 0.6,
  //   Level III-3 (aetiology): selected on the outcome, exposure recalled afterwards — open to
  //   recall bias and to how controls are chosen. Still the right design for a rare outcome.
  "Case-Control": 0.5,

  // ── Human data without a concurrent comparison or without temporality ────────────────────
  // Level IV. NHMRC does not order these against each other, so they weigh the same: an
  // uncontrolled study has no counterfactual, and a cross-sectional one compares groups but
  // measures exposure and outcome at the same moment, so it cannot say which came first.
  "Uncontrolled Trial": 0.4,
  "Uncontrolled Prospective Cohort": 0.4,
  "Uncontrolled Retrospective Cohort": 0.4,
  "Cross-Sectional": 0.4,
  //   One level below: systematic human data whose design is none of the named ones (mostly
  //   ecological), so the leaf says only what it is not. Weaker than any named human design,
  //   stronger than evidence that is not about humans.
  "Other Observational": 0.3,

  // ── No human outcome data, or a single case ──────────────────────────────────────────────
  // Outside NHMRC's levels for a claim about people: a result in animals or cells has to be
  // translated, and an anecdote has no design at all. Animal and Cell weighs the sum of its two
  // parts, an in-vivo result with in-vitro support being a little stronger than either alone.
  "Animal and Cell": 0.15,
  Animal: 0.1,
  Cell: 0.05,
  Anecdotal: 0.02,
};

/** A source's displayed quality rating, from weakest to strongest. */
export const QUALITY_RATINGS = ["low", "moderate", "high", "gold"] as const;
export type QualityRating = (typeof QUALITY_RATINGS)[number];

/**
 * The cross_design_quality at which each rating starts (design-documents/039 §2). A rating
 * starts where the weakest design of its group lands with neutral execution — the same neutral
 * value an unannotated dimension stands at — so the rating follows the score, and the score
 * follows both the design and how well it was carried out:
 *   - a study executed better than neutral can reach the rating above when its design sits close
 *     to that rating's weakest design (a well-run comparative prospective cohort reaches gold);
 *   - a study executed worse than neutral falls below its group when its design is that group's
 *     weakest (a poorly run case-control is moderate).
 * The rating is never decided by the design alone: two studies of one design with different
 * execution can be rated differently.
 */
export const QUALITY_RATING_FLOORS: Record<Exclude<QualityRating, "low">, number> = {
  // Randomised with a concurrent control: the RCT is the group's only design.
  gold: STUDY_TYPE_WEIGHTS.RCT * SEARCH_CONFIG.unansweredExecutionPrior,
  // A concurrent comparison group in humans: case-control is the group's weakest design.
  high: STUDY_TYPE_WEIGHTS["Case-Control"] * SEARCH_CONFIG.unansweredExecutionPrior,
  // Human data without a concurrent comparison or without temporality: other observational is
  // the group's weakest design. Animal, cell and anecdotal evidence fall below it.
  moderate: STUDY_TYPE_WEIGHTS["Other Observational"] * SEARCH_CONFIG.unansweredExecutionPrior,
};

/** Rates a source for display from its cross_design_quality. */
export function rateCrossDesignQuality(crossDesignQuality: number): QualityRating {
  if (crossDesignQuality >= QUALITY_RATING_FLOORS.gold) return "gold";
  if (crossDesignQuality >= QUALITY_RATING_FLOORS.high) return "high";
  if (crossDesignQuality >= QUALITY_RATING_FLOORS.moderate) return "moderate";
  return "low";
}

/** No discount when study_type can't be trusted — mirrors DEFAULT_SCORING_PROFILE's fallback. */
export const DEFAULT_DESIGN_WEIGHT = 1.0;

/**
 * Whether a source's study_type classification is trusted enough to drive a custom scoring
 * profile and a differentiated design weight. Reuses INGEST.tier1ConfidenceFloor rather than
 * inventing a new threshold. Recompute at scoring time, not cached at annotation time — a human
 * correction or a re-extraction changes study_type/confidence in place.
 */
export function isStudyTypeTrusted(
  studyTypeState: DimensionReviewState,
  studyTypeConfidence: number | null
): boolean {
  const humanTouched =
    studyTypeState !== "empty" &&
    ["human_filled", "ai_overridden", "ai_approved"].includes(studyTypeState);
  return humanTouched || (studyTypeConfidence ?? 0) >= INGEST.tier1ConfidenceFloor;
}

/**
 * Selects the scoring profile for a source given its study_type labels and trust. Untrusted or
 * unmatched designs fall back to DEFAULT_SCORING_PROFILE. study_type is never scored — trusted
 * or not — so it's stripped defensively even if a DESIGN_SCORING_PROFILES entry mistakenly
 * includes it.
 */
export function selectScoringProfile(
  studyTypeLabels: string[],
  trusted: boolean
): DesignScoringProfile {
  const topLabel = studyTypeLabels[0]; // study_type is single-select
  const chosen: DesignScoringProfile =
    (trusted && topLabel && isStudyTypeLabel(topLabel) && DESIGN_SCORING_PROFILES[topLabel]) ||
    DEFAULT_SCORING_PROFILE;
  return { includedDimensions: chosen.includedDimensions.filter((d) => d !== "study_type") };
}

/**
 * Selects the study type weight for a source given its study_type labels and trust.
 * Untrusted classifications stay neutral at DEFAULT_DESIGN_WEIGHT rather than differentiating
 * by a possibly-wrong label.
 */
export function selectDesignWeight(studyTypeLabels: string[], trusted: boolean): number {
  if (!trusted) return DEFAULT_DESIGN_WEIGHT;
  const topLabel = studyTypeLabels[0];
  return topLabel && isStudyTypeLabel(topLabel)
    ? STUDY_TYPE_WEIGHTS[topLabel]
    : DEFAULT_DESIGN_WEIGHT;
}

/**
 * Restricts SCORECARD's dimension map to only those applicable to a document's study type.
 * study_type itself is always included regardless of the scoring profile's exclusion — its
 * own confidence is what determines trust in the first place, so it must always be checked.
 *
 * Reused everywhere a per-field loop iterates every dimension unconditionally: ingest gating,
 * rank stability, point-gain ordering, and tier-cleared status all need to skip inapplicable
 * fields (corpus/internal/scorecard_scoring.ts) — a separate concern from excluding study_type
 * from the quality score (that's scoring.ts's computeDesignExecutionPoints; this is corpus-side
 * workflow, not claim-verification scoring).
 */
export function effectiveDimensions(
  studyTypeLabels: string[],
  trusted: boolean,
  dimensions: Record<ScorecardKey, DimensionConfig> = SCORECARD
): Record<ScorecardKey, DimensionConfig> {
  const profile = selectScoringProfile(studyTypeLabels, trusted);
  const included = new Set<ScorecardKey>([...profile.includedDimensions, "study_type"]);
  return Object.fromEntries(
    Object.entries(dimensions).filter(([key]) => included.has(key as ScorecardKey))
  ) as Record<ScorecardKey, DimensionConfig>;
}
